// Troca de senha obrigatória do admin novo (`spec/03-auth.md` §4.2, gate
// `password_change_required`). O Better Auth pede a senha atual no corpo, então esta
// rota NÃO tem step-up (`spec/17-frontend.md` §4.3).

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { changePassword } from '../api/auth.ts';
import { errorMessageOf } from '../api/errors.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';

const schema = z.object({
  currentPassword: z.string().min(1, 'Informe a senha atual'),
  newPassword: z.string().min(8, 'A senha precisa de pelo menos 8 caracteres'),
});

function ChangePassword() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    setError(undefined);
    try {
      await changePassword({ ...values, revokeOtherSessions: true });
      await queryClient.invalidateQueries();
      await navigate({ to: '/admin' });
    } catch (caught) {
      setError(errorMessageOf(caught));
    }
  });

  return (
    <AuthShell title="Troque a senha" description="A senha temporária só vale para esta troca.">
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="current-password">Senha atual</Label>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            className="h-11 bg-input"
            {...form.register('currentPassword')}
          />
          <FormError message={form.formState.errors.currentPassword?.message} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="admin-new-password">Senha nova</Label>
          <Input
            id="admin-new-password"
            type="password"
            autoComplete="new-password"
            className="h-11 bg-input"
            {...form.register('newPassword')}
          />
          <FormError message={form.formState.errors.newPassword?.message} />
        </div>
        <FormError message={error} />
        <Button type="submit" className="h-11" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Salvando…' : 'Salvar senha'}
        </Button>
      </form>
    </AuthShell>
  );
}

export const Route = createFileRoute('/admin/change-password')({ component: ChangePassword });
